import type { ComponentType } from "react";
import { useMemo } from "react";
import classNames from "classnames";
import { keyBy } from "lodash";
import { useTranslation } from "react-i18next";
import type { Field } from "@/entities/form-metadata";
import type { GridSpan } from "@/shared/components/Grid";
import { Grid } from "@/shared/components/Grid";
import { Show } from "@/shared/components/Show";
import { GroupRemoveItem } from "./GroupRemoveItem";
import type { BaseFieldSet, FieldMapperProps, FieldsGridConfig, FieldsGridGroup } from "./types";
import styles from "./FieldsGridRow.module.scss";

interface FieldsGridRowProps<FieldSet, FieldGroup, AssertionsEnum extends string> {
    instanceIndex: number;
    fieldset: FieldSet;
    fieldGroup: FieldGroup;
    gridConfig: FieldsGridConfig<AssertionsEnum>;
    className?: classNames.Argument;
    FieldMapper: ComponentType<FieldMapperProps<AssertionsEnum>>;
    onRemove: () => void;
    fieldDataPath: string;
}
export function FieldsGridRow<
    AssertionsEnum extends string,
    FieldSet extends BaseFieldSet<AssertionsEnum>,
    FieldGroup extends FieldsGridGroup<AssertionsEnum, FieldSet>,
>({
    instanceIndex,
    fieldset,
    fieldGroup,
    gridConfig,
    className,
    FieldMapper,
    onRemove,
    fieldDataPath,
}: FieldsGridRowProps<FieldSet, FieldGroup, AssertionsEnum>) {
    const { t } = useTranslation();
    const { assertions } = fieldGroup;

    const fieldByAssertionMap = useMemo(() => keyBy(fieldset.fields, "assertion"), [fieldset]);

    const RemoveItemController = gridConfig.Removable && (
        <GroupRemoveItem
            ariaLabel={t("removeInstance", {
                instanceName: fieldset.label || "",
                instanceIndex: instanceIndex + 1,
            })}
            removeItem={onRemove}
            testId={`removeInstance-${fieldDataPath}`}
        />
    );

    return (
        <fieldset>
            <Grid.Container className={classNames(styles.gridRow, className)}>
                <Grid.Field spanDesktop={gridConfig.FieldSetName} spanTablet={12} spanMobile={12}>
                    {/* Desktop legend */}
                    <Show.Above breakpoint={700}>
                        <legend className={styles.gridRowLabel} data-testid="grid-row-label">
                            {fieldset.label}
                        </legend>
                    </Show.Above>

                    {/* Mobile legend wrapper */}
                    <Show.Below breakpoint={700}>
                        <Grid.Container className={styles.gridRowLabelWrapper}>
                            <Grid.Field spanTablet={11} spanMobile={11}>
                                <legend className={styles.gridRowLabel}>{fieldset.label}</legend>
                            </Grid.Field>
                            {gridConfig.Removable && (
                                <Grid.Field spanTablet={1} spanMobile={1}>
                                    <div className={styles.removable}>{RemoveItemController}</div>
                                </Grid.Field>
                            )}
                        </Grid.Container>
                    </Show.Below>
                </Grid.Field>

                <Grid.Field
                    spanDesktop={(12 - gridConfig.FieldSetName - (gridConfig.Removable ? 1 : 0)) as GridSpan}
                    spanTablet={12}
                    spanMobile={12}
                >
                    <Grid.Container className={styles.gridRowAssertionsContainer}>
                        {assertions.map(({ assertion }) => {
                            const fieldData = fieldByAssertionMap[assertion];

                            return (
                                <Grid.Field
                                    key={assertion}
                                    spanDesktop={gridConfig.Assertions.desktop[assertion]}
                                    spanTablet={gridConfig.Assertions.tablet[assertion]}
                                    spanMobile={gridConfig.Assertions.mobile[assertion]}
                                    className={!fieldData ? styles.hiddenField : undefined}
                                >
                                    <FieldMapper
                                        fieldData={fieldData as Field<AssertionsEnum>}
                                        fieldPath={fieldDataPath}
                                        assertion={assertion}
                                        inputProperties={{
                                            "aria-label": `${fieldset.label ?? ""} ${fieldData?.label ?? ""}`,
                                        }}
                                    />
                                </Grid.Field>
                            );
                        })}
                    </Grid.Container>
                </Grid.Field>

                {/* Desktop remove button */}
                {gridConfig.Removable && (
                    <Show.Above breakpoint={700}>
                        <Grid.Field spanDesktop={1} spanTablet={1} spanMobile={1}>
                            <div className={styles.removable}>{RemoveItemController}</div>
                        </Grid.Field>
                    </Show.Above>
                )}
            </Grid.Container>
        </fieldset>
    );
}
